import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ChevronUp, ChevronDown, X } from "lucide-react";
import { Rocket02Icon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useOnboardingProgress } from "@/hooks/onboarding/useOnboardingProgress";
import { useArtistOnboardingState } from "@/hooks/onboarding/useArtistOnboardingState";
import { useOnboarding } from "@/contexts/OnboardingContext";

// Keeps the floating pill clear of the page footer (present on Home and a
// handful of marketing pages) — it's fixed to the viewport, so without this
// it would sit on top of whatever footer content scrolls into that spot.
// The footer usually isn't in the DOM yet on first paint (it's inside the
// route's own lazy-loaded/Suspense-boundary content, mounted after this
// component), so a MutationObserver picks it up whenever it actually appears
// or disappears, rather than only checking once on route change.
function useFooterClearance(pathname: string) {
  const [clearance, setClearance] = useState(0);
  const frameRef = useRef<number>();

  useEffect(() => {
    let detach: (() => void) | null = null;

    const attach = (footer: HTMLElement) => {
      const measure = () => {
        const overlap = window.innerHeight - footer.getBoundingClientRect().top;
        setClearance(overlap > 0 ? overlap + 12 : 0);
      };
      const onScrollOrResize = () => {
        if (frameRef.current) cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(measure);
      };
      measure();
      window.addEventListener("scroll", onScrollOrResize, { passive: true });
      window.addEventListener("resize", onScrollOrResize);
      detach = () => {
        if (frameRef.current) cancelAnimationFrame(frameRef.current);
        window.removeEventListener("scroll", onScrollOrResize);
        window.removeEventListener("resize", onScrollOrResize);
      };
    };

    const existing = document.querySelector<HTMLElement>("[data-app-footer]");
    if (existing) attach(existing);
    else setClearance(0);

    const observer = new MutationObserver(() => {
      const footer = document.querySelector<HTMLElement>("[data-app-footer]");
      if (footer && !detach) {
        attach(footer);
      } else if (!footer && detach) {
        detach();
        detach = null;
        setClearance(0);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      detach?.();
    };
  }, [pathname]);

  return clearance;
}

export function ArtistOnboardingChecklist() {
  const [expanded, setExpanded] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { steps, completedCount, total, percentage, allDone } =
    useOnboardingProgress();
  const { state, dismissChecklist, isLoading } = useArtistOnboardingState();
  const { startTour } = useOnboarding();
  const footerClearance = useFooterClearance(location.pathname);

  // Only persist dismissal when all steps are complete.
  // If incomplete, the box reappears on next login/visit.
  if (isLoading) return null;
  if (state?.checklist_dismissed_at && allDone) return null;


  const handleStepClick = (stepIndex: number) => {
    const step = steps[stepIndex];
    const params: Record<string, string> = { tab: step.tab };
    if (step.subtab) params.subtab = step.subtab;
    const onDashboard = location.pathname.startsWith("/dashboard/artist");
    if (onDashboard) {
      setSearchParams(params);
      setExpanded(false);
      setTimeout(() => startTour(step.id), 250);
    } else {
      const qs = new URLSearchParams(params).toString();
      navigate(`/dashboard/artist?${qs}`);
      setExpanded(false);
    }
  };

  return (
    <div
      className={cn(
        "fixed right-4 sm:right-6 z-[45] transition-transform duration-150",
        "bottom-[88px] sm:bottom-6",
      )}
      style={
        footerClearance > 0
          ? { transform: `translateY(-${footerClearance}px)` }
          : undefined
      }
    >
      {expanded ? (
        <div className="w-[min(92vw,360px)] light bg-card border border-border/50 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={Rocket02Icon} className="h-4 w-4 text-primary-light" />
              <span className="font-semibold text-sm text-foreground">
                Getting started
              </span>
              <span className="text-xs text-muted-foreground">
                {completedCount}/{total}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setExpanded(false)}
                className="p-1 text-muted-foreground hover:text-foreground rounded"
                aria-label="Collapse checklist"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              {allDone && (
                <button
                  onClick={() => dismissChecklist()}
                  className="p-1 text-muted-foreground hover:text-foreground rounded"
                  aria-label="Dismiss checklist"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

          </div>

          <div className="px-4 pt-3">
            <Progress value={percentage} className="h-1.5" />
          </div>

          <ul className="p-2 max-h-[60vh] overflow-y-auto">
            {steps.map((s, i) => (
              <li key={s.id}>
                <button
                  onClick={() => handleStepClick(i)}
                  className={cn(
                    "w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors",
                    "hover:bg-accent/50",
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                      s.done
                        ? "bg-primary-light/15 border-primary-light text-primary-light"
                        : "border-border text-transparent",
                    )}
                  >
                    {s.done && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span
                      className={cn(
                        "block text-sm font-medium",
                        s.done
                          ? "text-muted-foreground line-through"
                          : "text-foreground",
                      )}
                    >
                      {s.label}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {s.description}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {allDone && (
            <div className="px-4 py-3 border-t border-border/40 bg-primary/5">
              <p className="text-sm text-foreground font-medium">
                You&apos;re all set 🎉
              </p>
              <Button
                size="sm"
                variant="ghost"
                className="mt-1 h-7 px-2 text-xs"
                onClick={() => dismissChecklist()}
              >
                Hide checklist
              </Button>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setExpanded(true)}
          className={cn(
            "flex items-center gap-2 light bg-card border border-border/50 rounded-full pl-3 pr-4 py-2",
            "hover:bg-accent/40 transition-colors",
          )}
        >
          <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-primary/10">
            <HugeiconsIcon icon={Rocket02Icon} className="h-3.5 w-3.5 text-primary-light" />
          </span>
          <span className="text-sm font-medium text-foreground">
            Getting started
          </span>
          <span className="text-xs text-muted-foreground">
            {completedCount}/{total}
          </span>
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground ml-1" />
        </button>
      )}
    </div>
  );
}
